// TryItStep.tsx — onboarding step 3 (spec D6, handoff §6 step "Try it"). A trimmed-down
// Playground: same SSE route (`POST /api/bots/:botId/playground/chat`) and the same shared
// `chat-bubbles.tsx` components Playground itself uses, but no retrieval-debug panel and no
// session-management chrome (New session / Stop) — this is a one-shot "ask it something"
// moment, not the full debugging tool. Logic mirrors `Playground.tsx`'s `sendMessage`
// closely on purpose: the SSE frame contract (`token | citation | error | done`) is shared,
// so drift here would mean drift there too.
import { useEffect, useRef, useState } from 'react';
import { Button } from '@repo/ui/components/button';
import type { EngineDone, EngineEvent } from '@repo/shared-types';
import { AssistantBubble, ErrorBubble, UserBubble } from '../chat-bubbles';
import { streamSSE } from '../../lib/sse';
import { useT } from '../../locales/context';

type Citation = Extract<EngineEvent, { type: 'citation' }>['citation'];

type ChatMessage =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'error'; text: string }
  | { id: string; role: 'assistant'; text: string; citations: Citation[]; unanswered: boolean };

function makeSessionKey(): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `ob-${Date.now().toString(36)}${random}`;
}

export function TryItStep({
  botId,
  pageCount,
  onNext,
}: {
  botId: string;
  pageCount: number;
  onNext: () => void;
}) {
  const t = useT();
  const [sessionKey] = useState(makeSessionKey);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [streamingCitations, setStreamingCitations] = useState<Citation[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamingText]);

  useEffect(() => () => abortRef.current?.abort(), []);

  async function sendMessage(text: string) {
    const controller = new AbortController();
    abortRef.current = controller;

    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', text }]);
    setIsSending(true);
    setStreamingText('');
    setStreamingCitations([]);

    let accText = '';
    let accCitations: Citation[] = [];

    try {
      for await (const frame of streamSSE(
        `/api/bots/${botId}/playground/chat`,
        { message: text, sessionKey },
        { signal: controller.signal },
      )) {
        if (frame.event === 'token') {
          accText += (frame.data as { text: string }).text;
          setStreamingText(accText);
        } else if (frame.event === 'citation') {
          accCitations = [...accCitations, frame.data as Citation];
          setStreamingCitations(accCitations);
        } else if (frame.event === 'error') {
          const message = (frame.data as { message: string }).message;
          setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'error', text: message }]);
        } else if (frame.event === 'done') {
          const done = frame.data as EngineDone;
          setMessages((prev) => [
            ...prev,
            {
              id: done.messageId ?? crypto.randomUUID(),
              role: 'assistant',
              text: accText,
              citations: accCitations,
              unanswered: done.unanswered,
            },
          ]);
        }
      }
    } catch (err) {
      const aborted = err instanceof Error && err.name === 'AbortError';
      if (!aborted) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'error',
            text: err instanceof Error ? err.message : t.onboarding.genericFallbackError,
          },
        ]);
      }
    } finally {
      setIsSending(false);
      setStreamingText(null);
      setStreamingCitations([]);
      abortRef.current = null;
    }
  }

  function handleSubmit() {
    const text = input.trim();
    if (!text || isSending) return;
    setInput('');
    void sendMessage(text);
  }

  const showEmptyState = messages.length === 0 && streamingText === null;

  return (
    <div className="flex flex-col gap-5 rounded-[14px] border bg-card p-8 shadow-el-1">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[26px] font-bold tracking-[-0.01em]">
          {t.onboarding.tryItHeading}
        </h1>
        <p className="text-sm text-muted-foreground">
          {pageCount > 0
            ? t.onboarding.tryItSubtitleWithPages({ n: pageCount })
            : t.onboarding.tryItSubtitleNoPages}
        </p>
      </div>

      <div className="flex min-h-[220px] flex-col gap-3 rounded-xl border p-4">
        <div ref={scrollRef} className="flex flex-1 flex-col gap-3 overflow-y-auto">
          {showEmptyState && (
            <p className="text-sm text-muted-foreground">{t.onboarding.tryItSamplePrompt}</p>
          )}
          {messages.map((message) => {
            if (message.role === 'user') return <UserBubble key={message.id} text={message.text} />;
            if (message.role === 'error') return <ErrorBubble key={message.id} text={message.text} />;
            return (
              <AssistantBubble
                key={message.id}
                text={message.text}
                citations={message.citations}
                unanswered={message.unanswered}
              />
            );
          })}
          {streamingText !== null && (
            <AssistantBubble text={streamingText} citations={streamingCitations} streaming />
          )}
        </div>

        <div className="mt-auto flex items-end gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={t.onboarding.tryItPlaceholder}
            className="h-[38px] flex-1 rounded-md border border-input bg-transparent px-3 text-[13.5px] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
          />
          <Button type="button" size="sm" onClick={handleSubmit} disabled={!input.trim() || isSending}>
            {isSending ? t.onboarding.tryItSending : t.onboarding.tryItSendCta}
          </Button>
        </div>
      </div>

      <Button type="button" onClick={onNext} className="h-[42px] w-full">
        {t.onboarding.tryItNextCta}
      </Button>
    </div>
  );
}
